import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { selectedRect, type TableRect } from '@tiptap/pm/tables';

/**
 * The look of a row lives on its cells: the writer puts widths, padding and fills in each
 * cell's style rather than in a stylesheet. prosemirror-tables' addRow creates bare cells, so
 * a row added by hand was narrower, unpadded and unfilled next to its neighbors. These
 * commands add the row and then dress it like the row the cursor was in.
 */
const INHERITED_CELL_ATTRS = ['style', 'class', 'colwidth', 'backgroundColor'];
const INHERITED_ROW_ATTRS = ['style', 'class', 'backgroundColor'];

const rowPosition = (table: ProseMirrorNode, tableStart: number, index: number) => {
  let pos = tableStart;
  for (let i = 0; i < index; i++) pos += table.child(i).nodeSize;
  return pos;
};

const inherited = (from: ProseMirrorNode, onto: ProseMirrorNode, keys: string[]) => {
  const attrs: Record<string, unknown> = { ...onto.attrs };
  for (const key of keys) {
    if (key in onto.attrs && from.attrs[key] != null) attrs[key] = from.attrs[key];
  }
  return attrs;
};

/** Dresses the row at rowIndex of the table starting at tableStart like reference. */
export const inheritRowAttrs = (
  tr: Transaction,
  tableStart: number,
  rowIndex: number,
  reference: ProseMirrorNode
) => {
  const table = tr.doc.nodeAt(tableStart - 1);
  if (!table || rowIndex >= table.childCount) return;
  const row = table.child(rowIndex);
  const rowPos = rowPosition(table, tableStart, rowIndex);
  tr.setNodeMarkup(rowPos, undefined, inherited(reference, row, INHERITED_ROW_ATTRS));

  // Cells pair up by position; a row with a different shape, because of a span, keeps its
  // own cells as they are rather than borrowing the wrong ones.
  if (row.childCount !== reference.childCount) return;
  let cellPos = rowPos + 1;
  for (let i = 0; i < row.childCount; i++) {
    const cell = row.child(i);
    const from = reference.child(i);
    if (from.attrs.colspan === cell.attrs.colspan) {
      tr.setNodeMarkup(cellPos, undefined, inherited(from, cell, INHERITED_CELL_ATTRS));
    }
    cellPos += cell.nodeSize;
  }
};

const rectOf = (state: Parameters<typeof selectedRect>[0]): TableRect | null => {
  try {
    return selectedRect(state);
  } catch {
    return null;
  }
};

export const TableRowInheritance = Extension.create({
  name: 'tableRowInheritance',

  addCommands() {
    return {
      addRowBeforeLikeSelected:
        () =>
        ({ state, chain }) => {
          const rect = rectOf(state);
          if (!rect) return false;
          const reference = rect.table.child(rect.top);
          return chain()
            .addRowBefore()
            .command(({ tr }) => {
              inheritRowAttrs(tr, rect.tableStart, rect.top, reference);
              return true;
            })
            .run();
        },
      addRowAfterLikeSelected:
        () =>
        ({ state, chain }) => {
          const rect = rectOf(state);
          if (!rect) return false;
          const reference = rect.table.child(rect.bottom - 1);
          return chain()
            .addRowAfter()
            .command(({ tr }) => {
              inheritRowAttrs(tr, rect.tableStart, rect.bottom, reference);
              return true;
            })
            .run();
        }
    };
  }
});
